import { Link03, Route } from "@untitledui/icons";
import type { CommissionItem } from "@mfa-aff/shared";
import { CommissionTimeline, Countdown, EmptyState, ExplainAmount, JalaliDate, REASONS, ReasonBadge, ReasonNote, formatNumber, isReasonCode } from "@mfa-aff/ui";
import emptyOrders from "@mfa-aff/ui/assets/illustrations/empty-orders.webp";
import { SlideoutMenu } from "@mfa-aff/ui/components/application/slideout-menus/slideout-menu";
import { Table, TableCard } from "@mfa-aff/ui/components/application/table/table";
import { Badge, BadgeWithDot } from "@mfa-aff/ui/components/base/badges/badges";
import { Button } from "@mfa-aff/ui/components/base/buttons/button";
import { COMMISSION_STATUS, commissionLines, explainFootnote, timelineData } from "../orders/labels";

const StatusCell = ({ c }: { c: CommissionItem }) => {
    if (isReasonCode(c.reasonCode) && (c.status === "held" || c.status === "reversed" || c.status === "void")) {
        return <ReasonBadge code={c.reasonCode} />;
    }
    return (
        <div className="flex flex-col items-start gap-1">
            <BadgeWithDot type="pill-color" size="sm" color={COMMISSION_STATUS[c.status].color}>
                {c.statusLabel}
            </BadgeWithDot>
            {c.status === "pending" && c.timeline.eligibleAt && <Countdown target={c.timeline.eligibleAt} icon={false} className="text-xs" />}
        </div>
    );
};

const AmountCell = ({ c }: { c: CommissionItem }) => {
    if (c.amountRial === "0") {
        return <span className="text-sm text-quaternary">بدون پورسانت</span>;
    }
    const e = commissionLines(c);
    return <ExplainAmount rial={c.amountRial} lines={e.lines} moneyProps={{ size: "sm", className: "font-semibold" }} footnote={explainFootnote(c)} />;
};

const TimelinePanel = ({ c }: { c: CommissionItem }) => {
    const e = c.rateBp > 0 ? commissionLines(c) : undefined;
    const reason = isReasonCode(c.reasonCode) ? c.reasonCode : null;
    return (
        <SlideoutMenu.Trigger>
            <Button size="sm" color="secondary" iconLeading={Route} aria-label={`مسیر پورسانت ${c.product}`}>
                <span className="max-md:sr-only">مسیر</span>
            </Button>
            <SlideoutMenu isDismissable>
                {({ close }) => (
                    <>
                        <SlideoutMenu.Header onClose={close} className="flex flex-col gap-1 pe-12">
                            <p className="text-xs font-medium text-tertiary">مسیر پورسانت</p>
                            <h2 className="text-lg font-semibold text-primary">{c.product}</h2>
                            <p className="text-sm text-tertiary">
                                {c.domainMasked && (
                                    <>
                                        <bdi dir="ltr" className="latin-digits">
                                            {c.domainMasked}
                                        </bdi>{" "}
                                        ·{" "}
                                    </>
                                )}
                                {c.channelLabel}
                            </p>
                        </SlideoutMenu.Header>
                        <SlideoutMenu.Content>
                            {e && c.amountRial !== "0" && (
                                <div className="flex flex-col gap-2 rounded-xl bg-success-primary p-4 ring-1 ring-utility-green-200 ring-inset dark:bg-accent-500/10 dark:ring-accent-500/25">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-sm text-tertiary">پورسانت این سفارش</span>
                                        <ExplainAmount rial={c.amountRial} lines={e.lines} moneyProps={{ size: "lg", className: "font-semibold" }} />
                                    </div>
                                    <p className="text-xs leading-5 text-tertiary">{e.sentence}</p>
                                </div>
                            )}
                            {c.reasonCode && c.status !== "held" && (
                                <div className="flex flex-col gap-3 rounded-xl bg-secondary p-4">
                                    {reason ? (
                                        <ReasonNote code={reason} />
                                    ) : (
                                        <p className="text-sm text-secondary">{[c.reasonText, c.nextStep].filter(Boolean).join(" ")}</p>
                                    )}
                                    {reason && REASONS[reason].claimable && (
                                        <Button size="sm" color="secondary" href="/orders?tab=claims" className="self-start">
                                            ثبت ادعا
                                        </Button>
                                    )}
                                </div>
                            )}
                            <CommissionTimeline data={timelineData(c.timeline)} className="pb-6" />
                        </SlideoutMenu.Content>
                    </>
                )}
            </SlideoutMenu>
        </SlideoutMenu.Trigger>
    );
};

/** Recent commissions (Fintech dashboard "Recent transactions" table). Each opens its timeline. */
export const RecentCommissions = ({ items }: { items: CommissionItem[] }) => (
    <TableCard.Root className="rounded-2xl">
        <TableCard.Header
            title="پورسانت‌های اخیر"
            badge={
                <Badge type="modern" size="sm">
                    {formatNumber(items.length)} مورد
                </Badge>
            }
            description="روی هر مبلغ بزنید تا محاسبه‌اش را ببینید؛ «مسیر» نشان می‌دهد پورسانت در کدام مرحله است و چرا."
            contentTrailing={
                <Button size="sm" color="secondary" href="/orders">
                    همهٔ سفارش‌ها
                </Button>
            }
        />
        {items.length === 0 ? (
            <EmptyState
                illustration={emptyOrders}
                size="sm"
                icon={Link03}
                title="هنوز پورسانتی ثبت نشده است"
                description="به محض اینکه کسی با لینک، کد تخفیف یا کد معرف شما خرید کند، پورسانتش اینجا با مبلغ و وضعیت دیده می‌شود."
                action={{ label: "ساخت لینک", href: "/links" }}
                className="py-12"
            />
        ) : (
        <Table aria-label="پورسانت‌های اخیر">
            <Table.Header>
                <Table.Head id="product" label="سفارش" isRowHeader className="w-full" />
                <Table.Head id="date" label="تاریخ" className="max-md:hidden" />
                <Table.Head id="status" label="وضعیت" className="max-sm:hidden" />
                <Table.Head id="amount" label="پورسانت" />
                <Table.Head id="action" />
            </Table.Header>
            <Table.Body items={items}>
                {(c) => (
                    <Table.Row id={c.id}>
                        <Table.Cell className="max-sm:ps-4 max-sm:pe-2">
                            <div className="flex min-w-0 flex-col gap-0.5">
                                <p className="text-sm font-medium text-primary">{c.product}</p>
                                <p className="text-xs text-tertiary">
                                    {c.domainMasked && (
                                        <>
                                            <bdi dir="ltr" className="latin-digits">
                                                {c.domainMasked}
                                            </bdi>{" "}
                                            ·{" "}
                                        </>
                                    )}
                                    {c.channelLabel}
                                </p>
                                <div className="pt-1 sm:hidden">
                                    <StatusCell c={c} />
                                </div>
                            </div>
                        </Table.Cell>
                        <Table.Cell className="text-nowrap max-md:hidden">
                            <JalaliDate value={c.timeline.paidAt ?? c.createdAt} className="text-sm text-tertiary" />
                        </Table.Cell>
                        <Table.Cell className="max-sm:hidden">
                            <StatusCell c={c} />
                        </Table.Cell>
                        <Table.Cell className="text-nowrap max-sm:px-2">
                            <AmountCell c={c} />
                        </Table.Cell>
                        <Table.Cell className="px-4 max-sm:ps-2 max-sm:pe-4">
                            <TimelinePanel c={c} />
                        </Table.Cell>
                    </Table.Row>
                )}
            </Table.Body>
        </Table>
        )}
    </TableCard.Root>
);

